/**
 * Utilidades para el manejo de paginación
 */

/**
 * Calcula la página correcta después de eliminar elementos.
 *
 * Esta función resuelve el problema común donde al eliminar el último elemento
 * de una página (que no sea la primera), la grilla queda vacía porque mantiene
 * el mismo índice de página.
 *
 * @param currentPage - Página actual (0-indexed)
 * @param pageSize - Tamaño de página
 * @param totalItemsBefore - Total de items ANTES de eliminar
 * @param itemsDeletedCount - Cantidad de items eliminados (default: 1)
 * @returns Nueva página correcta (0-indexed)
 *
 * @example
 * // Escenario: Página 2, pageSize 5, total 11 items, eliminando 1 item
 * // Página 2 tiene solo 1 item (el item #11)
 * const newPage = calculatePageAfterDelete(2, 5, 11, 1)
 * // newPage = 1 (debe ir a página anterior porque la página 2 quedaría vacía)
 *
 * @example
 * // Escenario: Página 1, pageSize 10, total 25 items, eliminando 1 item
 * // Página 1 tiene items del 11 al 20 (10 items)
 * const newPage = calculatePageAfterDelete(1, 10, 25, 1)
 * // newPage = 1 (se mantiene en la misma página porque aún quedan 9 items)
 */
export const calculatePageAfterDelete = (
  currentPage: number,
  pageSize: number,
  totalItemsBefore: number,
  itemsDeletedCount: number = 1
): number => {
  // Total de items después de eliminar
  const totalItemsAfter = totalItemsBefore - itemsDeletedCount

  // Si no quedan items, ir a página 0
  if (totalItemsAfter <= 0) {
    return 0
  }

  // Calcular total de páginas después de eliminar (0-indexed)
  const totalPagesAfter = Math.ceil(totalItemsAfter / pageSize) - 1

  // Si la página actual es mayor que el total de páginas, ir a la última página
  if (currentPage > totalPagesAfter) {
    return totalPagesAfter
  }

  // Calcular items en la página actual ANTES de eliminar
  const itemsInCurrentPageBefore = Math.min(
    pageSize,
    totalItemsBefore - currentPage * pageSize
  )

  // Si eliminamos TODOS los items de la página actual, ir a la página anterior
  if (itemsDeletedCount >= itemsInCurrentPageBefore) {
    return Math.max(0, currentPage - 1)
  }

  // Si aún quedan items en la página actual, mantenerla
  return currentPage
}
